import { useState } from "react";
import { PaymentMethodPicker } from "@/features/marketplace/components/PaymentMethodPicker";
import { Link } from "react-router-dom";
import {
  useLabAcquisitions,
  useLabAcquisitionPayment,
  type LabAcquisition,
} from "@/lib/api/labAcquisitionHooks";

/** Owned purchases are recoverable even when the general Labs experiment is
 * control. This surface never pitches a new offer or adds another purchase. */
export function LabAcquisitionStatus({
  includePaid = true,
}: {
  includePaid?: boolean;
}) {
  const query = useLabAcquisitions();
  const items =
    query.data?.items.filter(
      (item) =>
        item.status !== "cancelled" && (includePaid || item.status !== "paid"),
    ) ?? [];
  if (!items.length) return null;
  return (
    <div className="space-y-12">
      {items.map((item) => (
        <AcquisitionStatusCard key={item.acquisition_id} item={item} />
      ))}
    </div>
  );
}
function AcquisitionStatusCard({ item }: { item: LabAcquisition }) {
  const payment = useLabAcquisitionPayment(item.acquisition_id);
  const [paymentMethod, setPaymentMethod] = useState<string | null>(null);
  const paid = item.status === "paid" || payment.paid;
  return (
    <section
      className="p-20 border border-line-subtle bg-raised"
      aria-label="Blood test purchase status"
    >
      <p className="type-label-xs text-ink-muted">Your blood test</p>
      <h2 className="font-display text-20 mt-8">
        {paid
          ? "Your blood test is confirmed."
          : item.status === "pending"
            ? "We’re confirming your blood test."
            : "Finish your blood test payment."}
      </h2>
      <p className="type-body-sm text-ink-secondary mt-10">{item.title}</p>
      <p className="type-body-sm text-ink-secondary mt-8">
        {new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(item.renewal_amount_cents / 100)}{" "}
        every{" "}
        {item.interval_months === 1
          ? "month"
          : `${item.interval_months} months`}
        . One kit per charge. Manage your subscription in Account.
      </p>
      {!paid && (
        <p className="type-body-sm text-ink-secondary mt-10">
          Your treatment purchase is separate and unchanged. Resume the blood
          test payment below to check its status or complete bank verification.
        </p>
      )}
      {payment.needsPaymentMethod && (
        <fieldset disabled={payment.busy} className="mt-12">
          <PaymentMethodPicker
            selectedPmId={paymentMethod}
            onSelect={setPaymentMethod}
            enabled
          />
        </fieldset>
      )}
      {payment.error && (
        <p className="type-body-sm text-danger mt-10" role="alert">
          {payment.error}
        </p>
      )}
      {paid ? (
        item.lab_order_id ? (
          <Link
            className="inline-flex min-h-44 items-center underline type-body-sm"
            to={`/app/labs/${item.lab_order_id}`}
          >
            View kit and results
          </Link>
        ) : (
          <p role="status" className="type-body-sm mt-10">
            Your kit details will appear shortly.
          </p>
        )
      ) : (
        <button
          className="inline-flex min-h-44 items-center underline type-body-sm disabled:opacity-50"
          disabled={
            payment.busy || (payment.needsPaymentMethod && !paymentMethod)
          }
          onClick={() =>
            void payment.resume(
              payment.needsPaymentMethod
                ? (paymentMethod ?? undefined)
                : undefined,
            )
          }
        >
          {payment.busy
            ? "Checking payment…"
            : payment.needsPaymentMethod
              ? "Retry saved payment"
              : "Resume blood test payment"}
        </button>
      )}
    </section>
  );
}
